import { Selection } from 'components/DataTable/DataTable';
import { useToastStore } from 'components/ToastManager/useToasts';
import { UserInvoice } from 'components/UserInvoice/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '@hooks/useApi';
import type { LedgerDecision } from '../LedgerDecisionDialog';
import { isLedgerDecisionError, useLedgerDecision } from './useLedgerDecision';

interface FinalizeOptions {
    selection?: Selection;
    filter?: any;
    selectedCategory?: string | null;
}

const POLL_INTERVAL = 2000;

// Batches finalize in the background, so polling has to survive a long run — but not
// forever, or a stuck batch keeps the tab busy until it is closed.
const POLL_TIMEOUT = 20 * 60 * 1000;

/**
 * Sends the finalize request and reports back once the invoices really are finalized.
 *
 * The endpoint answers in two shapes: a single invoice comes back finalized and ready to
 * be swapped into the list, everything else comes back as a progress toast for a queued
 * batch. In the batch case the list must not be reloaded right away — it would show the
 * very same drafts — so the batch is polled and `onFinished` fires when it is done.
 */
export const useFinalizeInvoices = (clubSlug: string, onFinished: (invoice?: UserInvoice) => void) => {
    const api = useApi();
    const { t } = useTranslation('invoice');
    const [isLoading, setIsLoading] = useState(false);
    // Which Mandant and dates are involved only the server knows here, so it is the one that asks.
    const ledgerDecision = useLedgerDecision();
    const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

    useEffect(
        () => () => {
            timers.current.forEach(clearTimeout);
            timers.current = [];
        },
        []
    );

    const pollBatch = useCallback(
        (batchId: string, startedAt: number) => {
            void api({
                url: `jobs/${batchId}/status`,
                method: 'GET',
                muted: true,
                onSuccess: ({ status, failed, failed_jobs, total_jobs }) => {
                    if (status === 'finished') {
                        setIsLoading(false);

                        // allowFailures() lets a batch finish with failed chunks, so "finished"
                        // alone would report a clean run over invoices that stayed drafts.
                        if (failed) {
                            useToastStore.getState().addToast({
                                type: 'error',
                                typeId: batchId,
                                title: t('invoiceList.actions.finalizeMultiple.failedTitle'),
                                description: t('invoiceList.actions.finalizeMultiple.failedDescription', {
                                    failed: failed_jobs,
                                    total: total_jobs
                                })
                            });
                        }

                        onFinished();

                        return;
                    }

                    if (Date.now() - startedAt > POLL_TIMEOUT) {
                        setIsLoading(false);

                        return;
                    }

                    timers.current.push(setTimeout(() => pollBatch(batchId, startedAt), POLL_INTERVAL));
                },
                onFailure: () => setIsLoading(false)
            });
        },
        [api, onFinished, t]
    );

    return {
        isLoading,
        finalize: useCallback(
            ({ selection, filter, selectedCategory }: FinalizeOptions) => {
                // 'all' is react-aria's select-all sentinel. It travels as its own flag: sent as
                // the invoice list it would arrive as an unusable id and the backend would have
                // to guess.
                const selectAll = selection === 'all';

                const send = (ledger_decision?: LedgerDecision) => {
                    setIsLoading(true);

                    void api({
                        url: `invoice/${clubSlug}/finalize`,
                        method: 'POST',
                        data: {
                            filter,
                            invoices: selectAll ? [] : (selection ?? []),
                            select_all: selectAll,
                            selectedCategory,
                            ...(ledger_decision && { ledger_decision })
                        },
                        muteWhen: (response) => !ledger_decision && isLedgerDecisionError(response),
                        onFailure: (response) => {
                            setIsLoading(false);
                            if (!ledger_decision) {
                                void ledgerDecision.retry(response).then((retried) => retried && send(retried));
                            }
                        },
                        onSuccess: (body) => {
                            const batchId = body?.type === 'toast' ? body?.data?.typeId : undefined;

                            if (batchId && body?.data?.type === 'progress') {
                                pollBatch(String(batchId), Date.now());

                                return;
                            }

                            setIsLoading(false);
                            onFinished(body?.data ?? undefined);
                        }
                    });
                };

                send();
            },
            [api, clubSlug, ledgerDecision, onFinished, pollBatch]
        )
    };
};

export default useFinalizeInvoices;
